import { Validators } from "@angular/forms";
import { MenuItem } from "primeng/api";
import { GCValidators } from "src/app/validators";
import { AuthType, ContactDomain, ContactType, ExternalPartnerUserData, FeatureFlags,
    FrontendPermissions, Gender, IAddress, IBankPaymentOption, IContact, ICreditCardPaymentOption, IDraft,
    Importance, IPayPalPaymentOption, IRemark,
    IUserBankAccount,
    IUserEx,
    UserState} from "@vierkant-software/types__api";
import { AppService } from "src/services/app.service";
import { IFECredentialInfo, IFrontendPaymentOption, IFrontendUser } from "./contract.interfaces";
import { UserContactStep } from "../shared-steps/contact/contacts.step";
import { UserPersonStep } from "./steps/person/person.step";
import { UserPhotoStep } from "./steps/photo/photo.step";
import { DONT_SAVE, DraftDefinition, ExitState, FC_G, GCFormArray, GCFormGroup, IAppService, StepDefinition } from "src/app/modules/ngx-draft";
import { DateTime } from "luxon";
import { ProjectsStep } from "./steps/projects/projects.step";
import { isSet } from "src/util/string";
import { UserPersonalDataStep } from "./steps/personal-data/personal-data.step";
import { UserBankDataStep } from "./steps/bank-data/bank-data.step";
import { ExternalPartnerStep } from "./steps/external-partner/external-partner.step";
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { formatIbanShort } from "src/util/formatting/bankAccount";

export class UserStepperDefinition implements DraftDefinition<IFrontendUser> { //FIXME: proper type

    private readonly CAN_EDIT_EGYM = (appService: IAppService) => appService.getFeature(FeatureFlags.egym)
        && appService.getFrontendPermission(FrontendPermissions.egym_manage);
    baseurl: string = '/users';
    breadcrumb: MenuItem[] = [{ label: 'Users' }, { label: 'new user' }];
    title = (form: FC_G<IFrontendUser>) => {
        const name = [form.value.firstname, form.value.lastname].join(' ').trim();
        return  isSet(name) ? name : 'Personendaten';
    };
    icon = "user";
    category = "Personendaten";
    public static readonly REQUIRED_CONTACT_AMOUNT: number = 1;

    controls = new GCFormGroup<IFrontendUser>({
        ID:            [''],
        firstname:     ['', [GCValidators.required, Validators.minLength(2)]],
        lastname:      ['', [GCValidators.required, Validators.minLength(2)]],
        birthday:      [undefined, [GCValidators.required, GCValidators.beforeDateTime(DateTime.now())]],
        gender:        [Gender.Undefined, [GCValidators.required]],
        isResponsible: [undefined],
        pronouns:      [''], //FIXME naming nominativ, genitiv
        pronouns2:     [''],
        userColor:     [undefined],
        defaultColor:  [undefined],
        // addressSearch: [''],
        addresses:     new GCFormArray<IAddress>({
            country:               [''],
            postcode:              [''],
            city:                  [''],
            street:                [''],
            houseNumber:           [''],
            additionalInformation: [''],
            description:           [''],
        }),
        onePaymentMethod: [''],
        paymentMethod:    new GCFormArray<IFrontendPaymentOption>({
            ID:             [''],
            user_ID:        [''],
            type:           [undefined, [GCValidators.required]],
            submittedLater: [false],
            primary:        [undefined], //FIXME string?????
            bankData:       new GCFormGroup<IBankPaymentOption>({
                owner: ['', [GCValidators.required,  Validators.minLength(3)]],
                IBAN:  ['', [GCValidators.required]],
                BIC:   [''], //@TODO: chris - conditiona validator - foreign IBAN might require BIC
            }),
            payPalData: new GCFormGroup<IPayPalPaymentOption>({
                email: ['', [GCValidators.required, Validators.email]],
            }),
            cardData: new GCFormGroup<ICreditCardPaymentOption>({
                owner:        ['', [GCValidators.required]],
                number:       ['', [GCValidators.required]],
                validTill:    [undefined, [GCValidators.required]],
                securityCode: ['', [GCValidators.required]],
            }),
        }),
        remarks: new GCFormArray<IRemark>({
            importance: [Importance.Unkown],
            content:    [''],
            validTill:  [undefined],
        }),
        image:         [''],
        recommendedBy: [''],
        contacts:      new GCFormArray<IContact>({
            domain:    [undefined, [GCValidators.required, GCValidators.notEqual(ContactDomain.Undefined)]],
            type:      [undefined, [GCValidators.required, GCValidators.notEqual(ContactType.Undefined)]],
            value:     [''],
            isPrimary: [undefined],
        }, undefined, undefined, UserStepperDefinition.validateContact),
        credentialInfo: new GCFormArray<IFECredentialInfo>({
            type:     [AuthType.Password, [GCValidators.required]], //TODO dynamic type
            username: ['', [GCValidators.required,  Validators.minLength(3)]],
            password: ['', [GCValidators.required]],
        }),
        prioritizedProjectIDs: [[]],
        employeeID:            [undefined],
        datevID:               [""],
        bankAccountList:       new GCFormArray<IUserBankAccount>({
            ID:                  [undefined],
            isActive:            [true],
            iban:                [undefined],
            bic:                 [undefined],
            accountQwner:        [undefined],
            defaultForContracts: [false],
            defaultForSales:     [false],
            createdAt:           [undefined],
            createdByID:         [undefined],
            modifiedAt:          [undefined],
            modifiedByID:        [undefined],
            ibanShort:           [undefined]
        }),
        differentContractHolder: new GCFormGroup<Partial<IFrontendUser>>({
            ID:        [''],
            firstname: ['', [GCValidators.required, Validators.minLength(2)]],
            lastname:  ['', [GCValidators.required, Validators.minLength(2)]],
            birthday:  [undefined, [GCValidators.required]],
            gender:    [Gender.Undefined, [GCValidators.required]],
            pronouns:  [''], //FIXME naming
            pronouns2: [''],
            addresses: new GCFormArray<IAddress>({
                country:               ['', [GCValidators.required, Validators.minLength(2)]],
                postcode:              ['', [GCValidators.required, Validators.minLength(5)]],
                city:                  ['', [GCValidators.required, Validators.minLength(2)]],
                street:                ['', [GCValidators.required, Validators.minLength(2)]],
                houseNumber:           ['', [GCValidators.required, Validators.minLength(1)]],
                additionalInformation: [''],
                description:           [''],
            }),
            contacts: new GCFormArray<IContact>({
                domain:    [ContactDomain.Undefined],
                type:      [ContactType.Undefined],
                value:     [''],
                isPrimary: [undefined],
            }, 'type', {
                [ContactType.EMail]:  { value: [GCValidators.required, Validators.email] },
                [ContactType.Phone]:  { value: [GCValidators.required] },
                [ContactType.Mobile]: { value: [GCValidators.required] },
                [ContactType.Fax]:    { value: [GCValidators.required] },
            }),
            credentialInfo: new GCFormGroup<IFECredentialInfo>({
                type:     [AuthType.Password, [GCValidators.required]], //TODO dynamic type
                username: ['', [GCValidators.required,  Validators.minLength(3)]],
                password: ['', [GCValidators.required]],
            }),
        }),
        // Aus dem Ticket geoeffnet liefert der Onlineabschluss keine Filiale mit - dann nicht verpflichtend.
        // Bewusst ohne Array, damit der `flags`-Getter von `conditional` erhalten bleibt (Pflichtfeld-Stern).
        branchID:            [undefined, GCValidators.conditional((form) => !form?.get('hideExtraSteps')?.value, GCValidators.required)],
        employeeBranch_ID:   [undefined],
        memberBranch_ID:     [undefined],
        externalPartnerData: new GCFormGroup<ExternalPartnerUserData>({
            egym: new GCFormGroup<UserState>({
                isTrainer: [false],
                email:     ['', [GCValidators.required]],
            })
        }),
        enableEGYM:     [false],
        hideExtraSteps: [false, undefined, undefined, DONT_SAVE],
        // without DONT_SAVE so a reload/navigate does not drop the ticket data
        ticketData:     new GCFormGroup<IFrontendUser['ticketData']>({
            IBAN:         [undefined],
            BIC:          [undefined],
            accountOwner: [undefined],
        }),
        }, UserStepperDefinition.validateContacts);



    static validateContact(data: FC_G<IContact>) {
        const errors: { [controlPath: string]: string | false } = {};
        const contact = data.value;
        let valid: boolean;
        switch(contact.type) {
            case ContactType.EMail:
                valid = contact.value?.split('@').filter(Boolean).length === 2;
                errors.value = valid ? false : 'EMAIL';
                break;
            case ContactType.Fax:
            case ContactType.Phone:
            case ContactType.Mobile:
                // https://regex101.com/r/CAVex8/143
                valid = /(\(?([\d \-\)\–\+\/\(]+){3,}\)?([ .\-–\/]?))/.test(contact.value);
                errors.value = valid ? false : contact.type === ContactType.Fax ? 'FAX' : 'PHONE';
                break;
        }
        return errors;
    }

    static addBankAccount(form: FC_G<IFrontendUser>, bankAccount: IUserBankAccount) {
        const user = form.value;
        //only one bank account at a time can be standard/for sales
        if (bankAccount.defaultForContracts)
            user.bankAccountList.forEach((account) => account.defaultForContracts = false);
        if (bankAccount.defaultForSales)
            user.bankAccountList.forEach((account) => account.defaultForSales = false);
        bankAccount.ibanShort = formatIbanShort(bankAccount.iban);
        user.bankAccountList.push(bankAccount);
        form.patchValue(user, {emitEvent: false});
    }

    static validateContacts(data: FC_G<IFrontendUser>) {
        const {contacts} = data.value;
        const errors: { [controlPath: string]: string | false } = {};
        if (contacts.length < UserStepperDefinition.REQUIRED_CONTACT_AMOUNT) errors.contacts = 'required';
        return errors;
    }

    steps = [
        new StepDefinition('Person', UserPersonStep, 'person', {
            create: () => <unknown>{
                remarks:   [{}],
                addresses: [{}],
                contacts:  [
                    { type: ContactType.EMail, domain: ContactDomain.Private, isPrimary: true },
                    { type: ContactType.Mobile, domain: ContactDomain.Private, isPrimary: true },
                ],
            },
            returnFromChild(data: ExitState, form, self, stepper) {
                form.patchValue(<unknown>{differentContractHolder: data?.data});
            },
            showSubmitButton: true,
            resolveData:      {
              branches(){
                return this.CheckInAreaWorker.getBranches();
              },
            }
        }),
        new StepDefinition('Anschrift', UserContactStep, 'contacts', {
            routeData: {showDescription: false},
        }),
        new StepDefinition('Foto', UserPhotoStep, 'photo', {
            hidden: ({ form }) => !!form.get('hideExtraSteps')?.value,
        }),
        new StepDefinition('Bankdaten', UserBankDataStep, 'bank-data', {
            hidden: ({ form }, self, stepper, appService) => !appService.getFeature(FeatureFlags.memberbox),
            returnFromChild(data: ExitState, form: FC_G<IFrontendUser>) {
                UserStepperDefinition.addBankAccount(form, data.data as IUserBankAccount);
            }
        }),
        new StepDefinition('Personaldaten', UserPersonalDataStep, 'personal-data', {
             hidden:      ({ form }) => !!form.get('hideExtraSteps')?.value,
             resolveData: {
                datevTenantInfos() {
                    return this.DatevWorker.getTenantInfos();
                 },
            },

        }),
        new StepDefinition('Externer Partner', ExternalPartnerStep, 'external-partner', {
            hidden: ({ form }, _self, _stepper, appService) =>
                !!form.get('hideExtraSteps')?.value || !this.CAN_EDIT_EGYM(appService),
        }),
        new StepDefinition('Projekte', ProjectsStep , 'projects', {
            hidden:      (status) => !status.draft.RecordID,
            resolveData: {
                projects({draftService}) { return this.ProjectWorker.getActiveProjectsOfUser(draftService.recordId); }
            },
        }),
    ];

    async fetch(uid: string, appService: AppService) {
        if (uid) {
            const user = await appService.api.UserWorker.getUser(uid, true, true);
            const data: IUserEx & { branchID?: string, image?: unknown, paymentMethod?: unknown } = user.data;   //TODO: Proper typing
            data.image = user.__files[0];
            const PO = await appService.api.UserWorker.getUserPaymentOptions(uid);
            data.paymentMethod = PO.map(x => ({ ...x.value, primary: x.isPrimary, ID: x.ID }));

            if (data.addresses?.length < 1)
                data.addresses = <IAddress[]><unknown>[{}];
            if (data.employeeBranch_ID)
                data.branchID = data.employeeBranch_ID;
            const result = {
                enableEGYM: !!data.externalPartnerData?.egym?.email,
                ...data,
            };
            return result as unknown as Record<string, unknown>;
        }
    }

    async final(form: FC_G<IFrontendUser>, draft: IDraft, appService: AppService): Promise<boolean | ExitState> {
        const data = form.getWithoutFiles() as unknown as IFrontendUser;
        delete data.defaultColor;
        delete data.ticketData;
        if (data.branchID) {
            data.employeeBranch_ID = data.branchID;
            data.memberBranch_ID = data.branchID;
        }
        delete data.branchID;
        const result = draft.RecordID ?
            await appService.api.UserWorker.upsertUser(data, data.ID) :
            await appService.api.UserWorker.createUser(data);
        draft.RecordID = result.ID;
        const img = await form.getFilesForSubmit();


        if (Object.keys(img).length > 0)
            await appService.api.UserWorker.uploadUserImage(img, result.ID);
        // deprecated. New backend will take care
        //await appService.api.AuthWorker.ensurePasswordCredential(draft.RecordID);

        return {
            buttons: [
                { label: _('PERSON.STEPPER.BUTTONS.CREATE_ANOTHER_PERSON'), link: ['%', 'users', 'person'] },
                { label: _('PERSON.STEPPER.BUTTONS.TO_PERSON_PROFILE'), link: ['/', 'person', draft.RecordID] }
            ],
            data: result };
    }
}
